import { TableRow, Cell, Periodicity, Entity } from '../..';
import { getUniqueValues } from '../../utils/helper';
import {
  AtomicConfigItemType,
  ConfigItem,
  ConfigItemOptions,
  ConfigItemType,
  ConfigItemValue,
  ConfigValue,
  DataTypeControlConfigValue,
  DateRangeConfigValue,
  isConfigGroup,
  MultiCellConfigValue,
  MultiSelectConfigValue,
  NumericRangeConfigValue,
  SelectConfigValue,
  ContextSelectorValue,
} from '../types';

const formatDate = (date: Date): string => {
  return new Date(date).toLocaleString('en-US', { month: 'long', year: 'numeric' });
};

/**
 * Formats the label for a context selector row.
 * @param row The selected row of context selector
 * @returns The formatted label for the context selector row
 */
export const formatContextSelectorLabel = (row: TableRow): string => {
  const { id, primaryLabel, secondaryLabel } = row;
  if (primaryLabel && secondaryLabel) {
    return `${primaryLabel} (${secondaryLabel})`;
  }
  if (primaryLabel) {
    return primaryLabel;
  }
  return id;
};

const configValueParsers: Record<AtomicConfigItemType, (configItemValues: ConfigValue[]) => string[]> = {
  [ConfigItemType.Select]: (selectConfigValues: SelectConfigValue[]) =>
    selectConfigValues.map(({ value, label }) => label ?? value),
  [ConfigItemType.MultiCell]: (multiCellConfigValues: MultiCellConfigValue[]) =>
    multiCellConfigValues.map(value => value.map(cell => cell.label)).flat(),
  [ConfigItemType.ContextSelector]: (selectedRows: ContextSelectorValue[]) =>
    selectedRows.map(rows => rows.map(formatContextSelectorLabel)).flat(),
  [ConfigItemType.MultiSelect]: (multiSelectConfigValues: MultiSelectConfigValue[]) =>
    multiSelectConfigValues.map(value => value.map(({ value, label }) => label ?? value)).flat(),
  [ConfigItemType.DataTypeControl]: (dataTypeControlConfigValues: DataTypeControlConfigValue[]) =>
    dataTypeControlConfigValues,
  [ConfigItemType.DateRange]: (dateRangeConfigValues: DateRangeConfigValue[]) =>
    dateRangeConfigValues
      .map(({ min, max }) => {
        if (min && max) {
          return `${formatDate(min)} till ${formatDate(max)}`;
        }
        if (min) {
          return `From ${formatDate(min)}`;
        }
        if (max) {
          return `Till ${formatDate(max)}`;
        }
      })
      .filter(Boolean),
  [ConfigItemType.NumericRange]: (numericRangeConfigValues: NumericRangeConfigValue[]) =>
    numericRangeConfigValues
      .map(({ min, max }) => {
        if (Number.isFinite(min) && Number.isFinite(max)) {
          return `${min} to ${max}`;
        }
        if (Number.isFinite(min)) {
          return `From ${min}`;
        }
        if (Number.isFinite(max)) {
          return `Up to ${max}`;
        }
      })
      .filter(Boolean),
};

export const getReadableConfigValues = (configItemType: ConfigItemType, configValues: ConfigValue[]): string[] => {
  return getUniqueValues(configValueParsers[configItemType]?.(configValues.filter(Boolean)) ?? []);
};

/**
 * Applies options to a config item. Traverses the config items and updates the options for the matching key.
 * BE AWARE: This function does not check the type of the config item, so the caller has to make sure the options fit the type.
 * @param items - Array of config items to apply options to.
 * @param options - Options to apply to the config item with the given key.
 * @returns A new array of config items with the options applied.
 */
export const applyOptions = (items: ConfigItem[], options: ConfigItemOptions): ConfigItem[] => {
  return items.map(i => {
    if (i.key === options.key) {
      return {
        ...i,
        options: options.options,
      } as ConfigItem;
    } else if (isConfigGroup(i)) {
      return {
        ...i,
        value: applyOptions(i.value, options),
      };
    }
    return i;
  });
};

/**
 * Applies a value to a config item. Traverses the config items and updates the value for the matching key.
 * BE AWARE: This function does not check the type of the config item, so the caller has to make sure the value fits the type.
 * @param items - Array of config items to apply the value to.
 * @param value - Value to apply to the config item with the given key.
 * @returns A new array of config items with the value applied.
 */
export const applyValue = (items: ConfigItem[], value: ConfigItemValue): ConfigItem[] => {
  return items.map(i => {
    if (i.key === value.key) {
      return {
        ...i,
        value: value.value,
      } as ConfigItem;
    } else if (isConfigGroup(i)) {
      return {
        ...i,
        value: applyValue(i.value, value),
      };
    }
    return i;
  });
};

/** Builds table rows for the context selector from the given cells.
 * Each row includes the latest monthly and weekly periods if available.
 * @param cells - Array of cells to convert into table rows.
 * @returns Array of table rows formatted for the context selector.
 */

export function buildContextSelectorRows(cells: Cell[]): TableRow[] {
  return cells.map((c, idx) => {
    const latestMonth = Array.isArray(c.periods)
      ? c.periods.find(p => p.periodicity === Periodicity.Monthly)
      : undefined;
    const latestWeek = Array.isArray(c.periods) ? c.periods.find(p => p.periodicity === Periodicity.Weekly) : undefined;

    const row: TableRow = {
      id: String(c.value ?? idx),
      productGroup: c.productName,
      country: c.countryName,
      ...(latestMonth && {
        latestMonth: {
          value: latestMonth.name,
          sortingValue: latestMonth.sequence,
        },
      }),
      ...(latestWeek && {
        latestWeek: {
          value: latestWeek.name,
          sortingValue: latestWeek.sequence,
        },
      }),
      primaryLabel: c.countryCode,
      secondaryLabel: c.productName,
      label: c.label,
    };

    return row;
  });
}

export const buildEntityFromSelectedRows = (selectedRows: TableRow[]): Entity[] => {
  return selectedRows.map(r => ({
    value: r.id,
    label: r.label || r.primaryLabel,
  })) as Entity[];
};
